import { withStyles, Button, Box, TextField, Icon, Domain } from 'vopay';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import styles from './Home.module.scss';
import Layout from '../Layout/Layout';
import { useDialog, OpenDialogProps, Dialog } from '@/providers/Dialog.provider';

const classes = withStyles(styles);

type HomeProps = {
  domain: Domain | null;
  onAddDomain: (domain: string) => void;
};

export default function Home({ domain, onAddDomain }: HomeProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Settings' });
  const dialog = useDialog();

  const handleAddDomain = async () => {
    const newDomain = await dialog.open(AddSenderDialog, { bare: true });

    if (!newDomain) return;

    onAddDomain(newDomain);
  };

  return (
    <Layout
      explanation={
        <p>
          Add domain names or email addresses on this page to allow SMTP2GO to send emails from them.{' '}
          <b>Sender domains</b> allow you to send from any <b>email addresses</b> at a domain and are verified via DNS
          records.
        </p>
      }
      content={
        <>
          <Button
            label={t('DomainSetup.Home.addSenderDomain', { defaultValue: 'Add Sender Domain' })}
            className={classes('addSenderButton')}
            onClick={handleAddDomain}
            isDisabled={domain !== null}
          />
        </>
      }
    />
  );
}

function AddSenderDialog({ onConfirm, onCancel }: OpenDialogProps<string>) {
  const { t } = useTranslation('translations', { keyPrefix: 'Settings' });

  return (
    <Box flx="col" ai="center" boundary="shadow" background="white" padding="24px 24px 60px" gap="md">
      <Icon icon="close2" onClick={onCancel} size="sm" className={classes('addSenderCloseButton')} />

      <Box flx="col" ai="center" gap="sm">
        <Dialog.Title level="3">
          {t('DomainSetup.Home.enterDomainName', { defaultValue: 'Enter your Domain Name' })}
        </Dialog.Title>

        <p>
          {t('DomainSetup.Home.domainNameDescription', {
            defaultValue: 'This will be the domain name of the sender email address',
          })}
        </p>
      </Box>

      <Formik
        initialValues={{ domain: '' }}
        onSubmit={(values) => {
          onConfirm(values.domain);
        }}
      >
        {({ handleSubmit, getFieldProps, values }) => (
          <form onSubmit={handleSubmit} className={classes('addSenderForm')}>
            <TextField {...getFieldProps('domain')} />

            <Button
              type="submit"
              isDisabled={values.domain === ''}
              label={t('DomainSetup.Home.continueWithDomain', { defaultValue: 'Continue with this domain' })}
              className={classes('addSenderFormSubmitButton')}
            />
          </form>
        )}
      </Formik>
    </Box>
  );
}
